import { ReactNode } from "react";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  ListOrderBy,
  ListRequest,
  useOmniOfferListQuery,
} from "@/services/backend/omni/offer/api/offer-api";
import { Offer } from "@/services/backend/omni/offer/types/offer";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

/**
 * OfferTable lists the offers of the request. showServiceKind adds a column
 * with the service kind of each offer, for lists mixing service kinds.
 */
export function OfferTable({
  request,
  onRequestChange,
  renderRowLink,
  showServiceKind = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (offerId: string, children: ReactNode) => ReactNode;
  showServiceKind?: boolean;
}) {
  const { data: list, isLoading, error } = useOmniOfferListQuery(request);

  const emptyList = list !== undefined && list.items.length === 0;
  const colSpan = showServiceKind ? 6 : 5;

  return (
    <div className="grid gap-2">
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              {showServiceKind && <TableHead>{t("Dienstleistung")}</TableHead>}
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Status")}</TableHead>
              <TableHead>{t("Angebotsdatum")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead>{t("Aktualisiert am")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={colSpan} />}
            {error && <TableRtkErrorRow error={error} colSpan={colSpan} />}
            {emptyList && (
              <TableEmptyRow
                colSpan={colSpan}
                text={t("Keine Angebote gefunden")}
              />
            )}
            {list?.items.map((offer) => (
              <OfferTableRow
                key={offer.id}
                offer={offer}
                renderRowLink={renderRowLink}
                showServiceKind={showServiceKind}
              />
            ))}
          </TableBody>
        </Table>
      </TableCard>
      {list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function OfferTableRow({
  offer,
  renderRowLink,
  showServiceKind,
}: {
  offer: Offer;
  renderRowLink: (offerId: string, children: ReactNode) => ReactNode;
  showServiceKind: boolean;
}) {
  return (
    <TableRow>
      {showServiceKind && <TableCell>{offer.serviceKind}</TableCell>}
      <TableCell>
        {renderRowLink(
          offer.id,
          <ReferenceNumber referenceNumber={offer.referenceNumber} />,
        )}
      </TableCell>
      <TableCell>
        <DocumentStateBadge document={offer} />
      </TableCell>
      <TableCell>{offer.date && formatDate(offer.date)}</TableCell>
      <TableCell>
        {formatDate(offer.createdAt)}, {formatTime(offer.createdAt)}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(offer.updatedAt)}, {formatTime(offer.updatedAt)}
      </TableCell>
    </TableRow>
  );
}

export function OfferTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<ListOrderBy>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Angebotsdatum"), value: "date" },
          { label: t("Nummer"), value: "referenceNumber" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
        ]}
      />
    </div>
  );
}
